import URI from "urijs";

import { isNotNil } from "@app/helpers/is-not-nil";
import { getBusbudEnv } from "@app/helpers/isomorphic-busbud-env";
import URIExpand from "@app/lib/uri-expand";

import type {
  SupportedLanguage,
  SupportedLocale,
  SupportedLocalesOrSubLocale
} from "../../../config/locales";
import type { MaybeWhitelabel } from "@partners/partner.type";

import {
  busbud_home_link_template,
  home_link_template
} from "../../../config/link-templates";
import { isSublocale } from "../../../config/locales";
import {
  isSubLocaleEquivalentToParent,
  supported_sub_locales
} from "../../../config/locales";

export type BuildAllResult = {
  canonical: string;
  fallback: string;
  androidapp?: string;
} & { [K in SupportedLocalesOrSubLocale]?: string };

export const getHomeUrl = ({
  query,
  whitelabel,
  locale,
  language = "en"
}: {
  query?: Record<string, unknown> | undefined;
  locale: SupportedLocalesOrSubLocale;
  language?: SupportedLanguage;
  whitelabel?: MaybeWhitelabel;
}): string => {
  // Does not work in local because of domain name
  // (redirects to /?whitelabel=neredennereye which does not exist)
  if (whitelabel === "neredennereye") {
    return getRootUrl(query, whitelabel);
  }

  // Recorrido doesn't use the `es-419` locale. When clicking to go gome, we are going to Recorrido website
  if (whitelabel === "recorrido") {
    if (!["en", "es"].includes(language)) {
      language = "en";
    }

    return buildUrl(
      URIExpand(home_link_template, {
        locale: language
      }),
      query,
      whitelabel
    );
  }

  const uri = URIExpand(home_link_template, {
    locale:
      isSublocale(locale) && isSubLocaleEquivalentToParent(locale)
        ? supported_sub_locales[locale].parent_locale
        : locale
  });

  return buildUrl(uri, query, whitelabel);
};

export const getBusbudHomeUrl = ({
  query,
  locale
}: {
  locale: SupportedLocale;
  query?: Record<string, unknown> | undefined;
}): string => {
  return buildUrl(
    URIExpand(busbud_home_link_template, {
      locale
    }),
    query
  );
};

export const buildUrl = (
  uri: URI,
  query?: Record<string, unknown> | undefined,
  whitelabel?: MaybeWhitelabel
): string => {
  const needs_post_processing = whitelabel || query;

  if (!needs_post_processing) {
    return uri.href();
  }

  if (query) {
    uri.addQuery(query);
  }

  if (whitelabel && !uri.hasQuery("whitelabel")) {
    // we can be in whitelabel mode configured in a specific domain,
    // and this case, we don't want to add the whitelabel query
    // But we can also be on busbud.com where the whitelabel query param is needed
    // To differentiate between both, we'll check the current URL: if it already
    // contains the whitelabel query, we'll keep it.
    // This will only works in the frontend.

    const current_whitelabel_conf =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("whitelabel")
        : "";

    if (
      getBusbudEnv() !== "production" ||
      current_whitelabel_conf === whitelabel
    ) {
      uri.addQuery({
        whitelabel: whitelabel
      });
    }
  }

  if (uri.hasQuery("whitelabel", "false")) {
    uri.removeQuery("whitelabel");
  }

  return uri.href();
};

const getRootUrl = (
  query?: Record<string, unknown> | undefined,
  whitelabel?: MaybeWhitelabel
): string => {
  return buildUrl(new URI("/"), query, whitelabel);
};

type SupportedLocaleLinks = {
  [K in SupportedLocalesOrSubLocale]?: string;
};

const getResultFallback = (result: SupportedLocaleLinks): string => {
  return result["en"] || getFirstResult(result);
};

const getFirstResult = (result: SupportedLocaleLinks): string => {
  const sorted_result_keys = Object.keys(result).sort();
  const first_result =
    result[sorted_result_keys[0] as SupportedLocalesOrSubLocale];
  return String(first_result);
};

export async function buildAllURIGenerator<
  A extends unknown[],
  L extends SupportedLocalesOrSubLocale
>(
  fn: (...args: [...A, L]) => string | Promise<string> | undefined,
  current_locale: L,
  locales: readonly L[],
  ...original_args: A
): Promise<BuildAllResult> {
  const result_pair = await Promise.all(
    locales.map(async locale => {
      const args_with_locale: [...A, L] = [...original_args, locale];

      const url_form_locale = await fn(...args_with_locale);

      return url_form_locale ? ([locale, url_form_locale] as const) : null;
    })
  );
  const result: SupportedLocaleLinks = Object.fromEntries(
    result_pair.filter(isNotNil)
  );

  const default_args: [...A, L] = [...original_args, current_locale];
  const default_canonical = await fn(...default_args);
  const fallback = getResultFallback(result);

  return {
    ...result,
    // If the current locale is not available, we set the fallback as canonical
    // But in practice, it should probably means that this page should be a 301 or a 404
    canonical:
      locales.includes(current_locale) && default_canonical
        ? default_canonical
        : fallback,
    fallback: fallback
  };
}
